import {
  lazy,
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { AlertCircle, ArrowUp, LoaderCircle } from "lucide-react";
import { ActiveFilters } from "./components/ActiveFilters";
import { AppHeader } from "./components/AppHeader";
import {
  DeferredChunkErrorBoundary,
  DeferredDetailFailure,
  DeferredDetailLoading
} from "./components/DeferredDetailBoundary";
import {
  type FacetCounts,
  FilterPanel
} from "./components/FilterPanel";
import { GroupDialog } from "./components/GroupDialog";
import { ReleaseList } from "./components/ReleaseList";
import { SmoothScroll } from "./components/SmoothScroll";
import type { SmoothScrollController } from "./components/SmoothScroll";
import {
  type CuratedModelTag,
  type CuratedSectionsOpen,
  DEFAULT_CURATED_SECTIONS_OPEN,
  FLAGSHIP_MODEL_IDS,
  MILESTONE_MODEL_IDS
} from "./data/curated-models";
import { useModelData } from "./hooks/use-model-data";
import { useVisitCutoff } from "./hooks/use-visit-cutoff";
import { useI18n } from "./hooks/use-i18n";
import {
  localizedRecordSearchText,
  ui
} from "./lib/localization";
import {
  countActiveFilters,
  EMPTY_FILTERS,
  filterModels,
  type GroupMode,
  type ReleaseFilterListKey,
  type ReleaseFilters,
  type ReleaseTagFilterInteraction,
  SORT_OPTIONS,
  type SortMode,
  sortModels,
  toggleReleaseFilterValue
} from "./lib/query-models";
import type { ModelRelease, TimelineRelease } from "./types/model";
import {
  buildGroupEntry,
  foldReleaseGroups,
  indexGroupMembers,
  parseGroupEntryId
} from "./lib/release-groups";
import { detailNavigation } from "./lib/detail-navigation";
import { buildOrganizationIndex } from "./lib/organizations";
import { gsap, outsideGsapContext, useGSAP } from "./lib/gsap";
import { prefetchWhenIdle } from "./lib/prefetch";
import {
  captureResultCardBounds,
  type ResultMotionKind,
  type ResultMotionSnapshot
} from "./lib/result-motion";

const PAGE_SIZE = 48;

// The detail overlay is the largest single view in the application and nothing
// renders it until a card is opened, so it ships as its own chunk. It is
// prefetched once the release log is idle, which keeps the open animation
// immediate without paying for the overlay during the first paint.
const loadDetailDialog = () => import("./components/DetailDialog");
const DetailDialog = lazy(() =>
  loadDetailDialog().then((module) => ({ default: module.DetailDialog }))
);

function countBy(values: string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return counts;
}

export default function App() {
  const { language, locale, t } = useI18n();
  const { payload, error } = useModelData();
  const visitCutoff = useVisitCutoff();
  const searchRef = useRef<HTMLInputElement>(null);
  const releaseSurfaceRef = useRef<HTMLElement>(null);
  const releaseContentRef = useRef<HTMLDivElement>(null);
  const smoothScrollController = useRef<SmoothScrollController | null>(null);
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [group, setGroup] = useState<GroupMode>("date");
  const [sort, setSort] = useState<SortMode>("newest");
  const [filters, setFilters] = useState<ReleaseFilters>(EMPTY_FILTERS);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selected, setSelected] = useState<ModelRelease | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  // The curated bar the open detail came from; previous and next walk it.
  const [selectedFrom, setSelectedFrom] = useState<CuratedModelTag | null>(null);
  const [keyboardIndex, setKeyboardIndex] = useState(-1);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [curatedOpen, setCuratedOpen] = useState(
    DEFAULT_CURATED_SECTIONS_OPEN
  );
  const toggleCurated = useCallback(
    (section: keyof CuratedSectionsOpen) =>
      setCuratedOpen((current) => ({
        ...current,
        [section]: !current[section]
      })),
    []
  );
  const filterMotionKey = JSON.stringify(filters);
  const previousFilterMotionKey = useRef(filterMotionKey);
  const resultMotionRequest = useRef<ResultMotionSnapshot | null>(null);
  const nextResultMotionId = useRef(0);
  const resultGhostLayer = useRef<HTMLDivElement | null>(null);
  const organizations = useMemo(
    () => buildOrganizationIndex(payload?.organizations ?? []),
    [payload]
  );
  const modelNamesById = useMemo(
    () =>
      new Map(
        (payload?.records ?? []).map((record) => [record.id, record.name])
      ),
    [payload]
  );

  const filtered = useMemo(
    () =>
      payload
        ? filterModels(payload.records, deferredQuery, filters, (record) =>
            localizedRecordSearchText(record, language)
          )
        : [],
    [deferredQuery, filters, language, payload]
  );
  // The timeline shows a release group as one card as soon as any member
  // matches (one card per category under the category grouping); the matched
  // records themselves still feed the curated sections, and a group card
  // counts as its matching members in the model counts.
  const timeline = useMemo(
    () =>
      payload
        ? foldReleaseGroups(filtered, payload.records, payload.modelGroups, {
            byCategory: group === "category"
          })
        : [],
    [filtered, group, payload]
  );
  const ordered = useMemo(
    () => sortModels(timeline, group, sort),
    [timeline, group, sort]
  );
  const selectedGroup = useMemo(() => {
    if (!selectedGroupId || !payload) return null;
    const current = timeline.find((entry) => entry.id === selectedGroupId);
    if (current?.groupView) return current;
    // No member matches any more: the panel stays open with every card dimmed.
    const entry = parseGroupEntryId(selectedGroupId);
    const group = entry
      ? payload.modelGroups.find((item) => item.id === entry.groupId)
      : undefined;
    const members = group
      ? indexGroupMembers(payload.records, [group])
          .get(group.id)
          ?.filter(
            (member) =>
              entry?.category === undefined ||
              member.mainCategory === entry.category
          )
      : undefined;
    return group && members?.length
      ? buildGroupEntry(group, members, [], selectedGroupId)
      : null;
  }, [payload, selectedGroupId, timeline]);
  const openEntry = useCallback((entry: TimelineRelease) => {
    if (entry.groupView) {
      // Reached from a member's overlay by previous/next: the panel replaces it.
      setSelected(null);
      setSelectedGroupId(entry.id);
    } else {
      setSelected(entry);
    }
  }, []);
  const openFromTimeline = useCallback(
    (entry: TimelineRelease) => {
      setSelectedFrom(null);
      openEntry(entry);
    },
    [openEntry]
  );
  const openFromCurated = useCallback(
    (record: ModelRelease, section: CuratedModelTag) => {
      setSelectedFrom(section);
      openEntry(record);
    },
    [openEntry]
  );
  const flagshipRecords = useMemo(() => {
    const filteredById = new Map(filtered.map((record) => [record.id, record]));
    return FLAGSHIP_MODEL_IDS.flatMap((modelId) => {
      const record = filteredById.get(modelId);
      return record ? [record] : [];
    });
  }, [filtered]);
  const milestoneRecords = useMemo(() => {
    const filteredById = new Map(filtered.map((record) => [record.id, record]));
    return MILESTONE_MODEL_IDS.flatMap((modelId) => {
      const record = filteredById.get(modelId);
      return record ? [record] : [];
    });
  }, [filtered]);
  const visible = useMemo(() => ordered.slice(0, limit), [limit, ordered]);
  const loadMore = useCallback(() => {
    setLimit((current) => Math.min(current + PAGE_SIZE, ordered.length));
  }, [ordered.length]);
  const activeFilterCount = countActiveFilters(filters);
  const resultGroupLabel = ui(
    language,
    group === "date"
      ? "groupDate"
      : group === "company"
        ? "groupCompany"
        : group === "category"
          ? "groupCategory"
          : "groupNone"
  );
  const resultSortLabel = ui(
    language,
    sort === "newest"
      ? "sortNewest"
      : sort === "oldest"
        ? "sortOldest"
        : sort === "latest"
          ? "sortLatestRelease"
          : sort === "count"
            ? "sortModelCount"
            : sort === "company"
              ? "sortCompany"
              : sort === "category"
                ? "sortCategory"
                : sort === "status"
                  ? "sortReleaseStatus"
                  : group === "company"
                    ? "sortCompanyName"
                    : group === "category"
                      ? "sortCategoryName"
                      : "sortModelName"
  );

  const facetCounts = useMemo<FacetCounts>(() => {
    const records = payload?.records ?? [];
    return {
      companies: countBy(records.flatMap((record) => record.organization.filterIds)),
      categories: countBy(records.map((record) => record.mainCategory)),
      capabilities: countBy(records.flatMap((record) => record.capabilities)),
      statuses: countBy(records.map((record) => record.release.status)),
      openness: countBy(records.map((record) => record.release.openness))
    };
  }, [payload]);

  const resetView = useCallback(() => {
    setLimit(PAGE_SIZE);
    setKeyboardIndex(-1);
  }, []);

  const clearResultGhosts = useCallback(() => {
    const layer = resultGhostLayer.current;
    if (!layer) return;
    gsap.killTweensOf(layer.querySelectorAll(".result-motion-ghost"));
    layer.remove();
    resultGhostLayer.current = null;
  }, []);

  const prepareResultMotion = useCallback(
    (
      kind: ResultMotionKind,
      originModelId?: string,
      nextModelIds?: ReadonlySet<string>
    ) => {
      clearResultGhosts();
      const cards = captureResultCardBounds(releaseContentRef.current);
      resultMotionRequest.current = cards.size
        ? {
            cards,
            id: ++nextResultMotionId.current,
            kind,
            ...(originModelId ? { originModelId } : {})
          }
        : null;

      if (
        kind === "search" ||
        !nextModelIds ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        return;
      }

      const surface = releaseSurfaceRef.current;
      const content = releaseContentRef.current;
      if (!surface || !content) return;

      const surfaceRect = surface.getBoundingClientRect();
      const leaving = Array.from(
        content.querySelectorAll<HTMLElement>(
          ".release-list--virtual .model-card-shell[data-model-id]"
        )
      )
        .filter((card) => {
          const modelId = card.dataset.modelId;
          return Boolean(modelId && !nextModelIds.has(modelId));
        })
        .map((card) => ({ card, rect: card.getBoundingClientRect() }))
        .filter(
          ({ rect }) =>
            rect.bottom > surfaceRect.top &&
            rect.top < surfaceRect.bottom &&
            rect.right > surfaceRect.left &&
            rect.left < surfaceRect.right
        );
      if (!leaving.length) return;

      const layer = document.createElement("div");
      layer.className = "result-motion-ghost-layer";
      layer.style.left = `${surfaceRect.left}px`;
      layer.style.top = `${surfaceRect.top}px`;
      layer.style.width = `${surfaceRect.width}px`;
      layer.style.height = `${surfaceRect.height}px`;

      const ghosts = leaving.map(({ card, rect }) => {
        const ghost = card.cloneNode(true) as HTMLElement;
        ghost.classList.add("result-motion-ghost");
        ghost.setAttribute("aria-hidden", "true");
        ghost.removeAttribute("data-wheel-active");
        ghost.style.left = `${rect.left - surfaceRect.left}px`;
        ghost.style.top = `${rect.top - surfaceRect.top}px`;
        ghost.style.width = `${rect.width}px`;
        ghost.style.height = `${rect.height}px`;
        ghost
          .querySelectorAll<HTMLElement>("button, a, input, select, textarea")
          .forEach((control) => {
            control.tabIndex = -1;
          });
        layer.append(ghost);
        return ghost;
      });

      document.body.append(layer);
      resultGhostLayer.current = layer;
      // The ghost layer lives in the document body and is retired by this
      // tween, so the tween has to outlive the component that requested the
      // filter change. Building it outside the caller's context keeps a card
      // that unmounts on the very same change from killing it.
      outsideGsapContext(() =>
        gsap.to(ghosts, {
          autoAlpha: 0,
          y: kind === "tag" ? -7 : -4,
          scale: 0.985,
          duration: kind === "tag" ? 0.16 : 0.12,
          ease: "power2.in",
          stagger: ghosts.length > 1 ? { amount: 0.035, from: "end" } : 0,
          overwrite: "auto",
          onComplete: () => {
            if (resultGhostLayer.current === layer) clearResultGhosts();
          }
        })
      );
    },
    [clearResultGhosts]
  );

  const matchingModelIds = useCallback(
    (nextQuery: string, nextFilters: ReleaseFilters) =>
      new Set(
        filterModels(
          payload?.records ?? [],
          nextQuery,
          nextFilters,
          (record) => localizedRecordSearchText(record, language)
        )
          .map((record) => record.id)
      ),
    [language, payload]
  );

  const applyFilters = useCallback(
    (
      nextFilters: ReleaseFilters,
      interaction?: ReleaseTagFilterInteraction
    ) => {
      prepareResultMotion(
        interaction?.source === "release-tag" ? "tag" : "filter",
        interaction?.originModelId,
        matchingModelIds(deferredQuery, nextFilters)
      );
      setFilters(nextFilters);
      resetView();
    },
    [deferredQuery, matchingModelIds, prepareResultMotion, resetView]
  );

  const handleQuery = (value: string) => {
    prepareResultMotion("search");
    setQuery(value);
    resetView();
  };
  const handleGroup = (value: GroupMode) => {
    setGroup(value);
    setSort(SORT_OPTIONS[value][0]!.value);
    resetView();
  };
  const handleSort = (value: SortMode) => {
    setSort(value);
    resetView();
  };
  const toggleFilter = useCallback(
    (
      key: ReleaseFilterListKey,
      value: string,
      interaction?: ReleaseTagFilterInteraction
    ) => {
      applyFilters(
        toggleReleaseFilterValue(filters, key, value),
        interaction
      );
    },
    [applyFilters, filters]
  );
  const clearAll = () => {
    prepareResultMotion(
      query ? "search" : "filter",
      undefined,
      matchingModelIds("", EMPTY_FILTERS)
    );
    setQuery("");
    setFilters(EMPTY_FILTERS);
    resetView();
  };

  useEffect(() => () => clearResultGhosts(), [clearResultGhosts]);

  useEffect(() => {
    if (!payload) return;
    return prefetchWhenIdle(loadDetailDialog);
  }, [payload]);

  const navigation = detailNavigation({
    group: selectedGroup,
    curated: selectedFrom,
    flagship: flagshipRecords,
    milestone: milestoneRecords,
    timeline: ordered
  });
  const selectedIndex = selected
    ? navigation.findIndex((record) => record.id === selected.id)
    : -1;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT" ||
        target?.isContentEditable;

      if (event.key === "Escape") {
        if (!selected && filterOpen) setFilterOpen(false);
        return;
      }
      if (typing) return;
      if (event.key === "/") {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (selected && (event.key === "j" || event.key === "ArrowRight")) {
        const next = selectedIndex >= 0 ? navigation[selectedIndex + 1] : undefined;
        if (next) openEntry(next);
        return;
      }
      if (selected && (event.key === "k" || event.key === "ArrowLeft")) {
        const previous = selectedIndex > 0 ? navigation[selectedIndex - 1] : undefined;
        if (previous) openEntry(previous);
        return;
      }
      if (selectedGroup) return;
      // The filter panel and an open Group or Sort menu sit over the list, so
      // their keys stay with them: a press from Reset, Done or a menu never
      // opens a model behind them.
      if (filterOpen || document.querySelector(".control-menu.is-open")) return;
      if (event.key === "j" || event.key === "k") {
        event.preventDefault();
        // The list's selection takes over from whatever control had focus, so
        // the next Enter opens the selected card and does not also press it.
        if (target && target !== document.body) target.blur();
        setKeyboardIndex((current) =>
          event.key === "j"
            ? Math.min(current + 1, visible.length - 1)
            : Math.max(current - 1, 0)
        );
      } else if (event.key === "Enter" && keyboardIndex >= 0) {
        const record = visible[keyboardIndex];
        if (record) openFromTimeline(record);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    filterOpen,
    keyboardIndex,
    navigation,
    openEntry,
    openFromTimeline,
    selected,
    selectedGroup,
    selectedIndex,
    visible
  ]);

  useEffect(() => {
    const record = visible[keyboardIndex];
    if (!record) return;
    // The list's own card: a Flagship model also renders inside the curated
    // section, which may be collapsed, and the first match would scroll there.
    releaseContentRef.current
      ?.querySelector(`.release-list [data-model-id="${CSS.escape(record.id)}"]`)
      ?.scrollIntoView({
        block: "nearest",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth"
      });
  }, [keyboardIndex, visible]);

  useEffect(() => {
    const surface = releaseSurfaceRef.current;
    if (!surface || !payload) return;

    const update = () => {
      const next = surface.scrollTop > Math.max(320, surface.clientHeight * 0.6);
      setShowBackToTop((current) => (current === next ? current : next));
    };

    update();
    surface.addEventListener("scroll", update, { passive: true });
    return () => surface.removeEventListener("scroll", update);
  }, [payload]);

  useGSAP(
    () => {
      const changed = previousFilterMotionKey.current !== filterMotionKey;
      previousFilterMotionKey.current = filterMotionKey;
      const target = releaseContentRef.current?.querySelector<HTMLElement>(
        ".results-meta"
      );
      if (!changed || !target) return;

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        gsap.set(target, { clearProps: "opacity,visibility,transform" });
        return;
      }

      gsap.fromTo(
        target,
        { autoAlpha: 0.55, y: -4 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.28,
          ease: "power2.out",
          clearProps: "opacity,visibility,transform"
        }
      );
    },
    {
      scope: releaseContentRef,
      dependencies: [filterMotionKey],
      revertOnUpdate: true
    }
  );

  if (error) {
    return (
      <main className="state-screen">
        <AlertCircle size={24} aria-hidden="true" />
        <h1>{t("modelDataLoadError")}</h1>
        <p role="alert">{error}</p>
      </main>
    );
  }

  if (!payload) {
    return (
      <main className="state-screen" aria-live="polite">
        <LoaderCircle className="spin" size={24} aria-hidden="true" />
        <h1>{t("loadingModelReleases")}</h1>
        <p>{t("loadingSnapshot")}</p>
      </main>
    );
  }

  return (
    <div className="app-shell">
      <div className="header-frame">
        <AppHeader
          query={query}
          searchRef={searchRef}
          group={group}
          sort={sort}
          activeFilterCount={activeFilterCount}
          onQuery={handleQuery}
          onGroup={handleGroup}
          onSort={handleSort}
          onOpenFilters={() => setFilterOpen(true)}
        />

        <ActiveFilters
          query={query}
          filters={filters}
          onClearQuery={() => handleQuery("")}
          onRemove={toggleFilter}
          onClearDates={() => {
            applyFilters({ ...filters, from: "", to: "" });
          }}
          onClearAll={clearAll}
          organizations={organizations}
        />
      </div>
      <main ref={releaseSurfaceRef} className="release-surface">
        <div ref={releaseContentRef} className="release-content">
          <header className="results-meta">
            <span>
              {visible.length === ordered.length
                ? language === "zh"
                  ? `${filtered.length.toLocaleString(locale)} / ${payload.records.length.toLocaleString(locale)} ${t("models")}`
                  : `${filtered.length.toLocaleString(locale)} ${t("of")} ${payload.records.length.toLocaleString(locale)} ${t("models")}`
                : language === "zh"
                  ? `${t("showing")} ${visible.length.toLocaleString(locale)} / ${ordered.length.toLocaleString(locale)} ${t("matches")}`
                  : `${t("showing")} ${visible.length.toLocaleString(locale)} ${t("of")} ${ordered.length.toLocaleString(locale)} ${t("matches")}`}
            </span>
            <span>
              {t("groupedBy")}{" "}
              {language === "en"
                ? resultGroupLabel.toLocaleLowerCase("en")
                : resultGroupLabel}{" "}
              ·{" "}
              {language === "en"
                ? resultSortLabel.toLocaleLowerCase("en")
                : resultSortLabel}
            </span>
          </header>

        {visible.length ? (
          <ReleaseList
            records={visible}
            countRecords={ordered}
            flagshipRecords={flagshipRecords}
            milestoneRecords={milestoneRecords}
            group={group}
            visitCutoff={visitCutoff}
            keyboardIndex={keyboardIndex}
            onOpen={openFromTimeline}
            onOpenCurated={openFromCurated}
            filters={filters}
            onToggleFilter={toggleFilter}
            animationKey={`${deferredQuery}:${group}:${sort}:${JSON.stringify(filters)}`}
            motionRequestRef={resultMotionRequest}
            scrollElementRef={releaseSurfaceRef}
            scrollControllerRef={smoothScrollController}
            hasMore={visible.length < ordered.length}
            onLoadMore={loadMore}
            organizations={organizations}
            curatedOpen={curatedOpen}
            onToggleCurated={toggleCurated}
          />
        ) : (
          <section className="empty-state">
            <h2>{t("noModelsMatch")}</h2>
            <p>{t("noModelsMatchHint")}</p>
            <button type="button" className="secondary-button" onClick={clearAll}>
              <span>{t("clearEverything")}</span>
            </button>
          </section>
        )}

        <footer className="app-footer">
          <span>
            {payload.records.length.toLocaleString(locale)}{" "}
            {t("curatedRecords")} · {t("data")} {payload.dataVersion}
          </span>
          <span>
            / {t("keyboardSearch")} · j k {t("keyboardMove")} · ↵{" "}
            {t("keyboardOpen")} · esc {t("keyboardClose")}
          </span>
        </footer>
        </div>
      </main>

      <SmoothScroll
        wrapperRef={releaseSurfaceRef}
        contentRef={releaseContentRef}
        routeCardTagGestures
        controllerRef={smoothScrollController}
      />

      {showBackToTop && !filterOpen && !selected && !selectedGroup ? (
        <button
          type="button"
          className="scroll-top-button"
          aria-label={t("backToTop")}
          title={t("backToTop")}
          onClick={() => smoothScrollController.current?.scrollToTop()}
        >
          <ArrowUp size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
      ) : null}

      {filterOpen ? (
        <FilterPanel
          open
          facets={payload.facets}
          filters={filters}
          counts={facetCounts}
          onToggle={toggleFilter}
          onDates={(from, to) => {
            applyFilters({ ...filters, from, to });
          }}
          onReset={() => {
            applyFilters(EMPTY_FILTERS);
          }}
          onClose={() => setFilterOpen(false)}
          organizations={organizations}
        />
      ) : null}

      {selectedGroup ? (
        <GroupDialog
          key={selectedGroup.id}
          entry={selectedGroup}
          organizations={organizations}
          filters={filters}
          onToggleFilter={toggleFilter}
          onOpenMember={setSelected}
          onClose={() => {
            setSelected(null);
            setSelectedGroupId(null);
          }}
          origin={() =>
            document.querySelector(`.release-list [data-model-id="${CSS.escape(selectedGroup.id)}"]`)
          }
          activeMemberId={selected?.id ?? null}
        />
      ) : null}

      {selected ? (
        <DeferredChunkErrorBoundary
          fallback={
            <DeferredDetailFailure
              closeLabel={t("closeDetails")}
              description={t("modelDetailsLoadErrorHint")}
              onClose={() => setSelected(null)}
              onReload={() => window.location.reload()}
              reloadLabel={t("reloadPage")}
              title={t("modelDetailsLoadError")}
            />
          }
        >
          <Suspense
            fallback={
              // Inside a group window the members have already stepped back
              // for the detail; a second loading box over them would only
              // flash before the detail grows out of the card.
              selectedGroup ? null : (
                <DeferredDetailLoading
                  closeLabel={t("closeDetails")}
                  description={t("loadingModelDetailsHint")}
                  onClose={() => setSelected(null)}
                  title={t("loadingModelDetails")}
                />
              )
            }
          >
            <DetailDialog
              record={selected}
              modelNamesById={modelNamesById}
              organizations={organizations}
              platformUsageRules={payload.platformUsageRules.filter((rule) =>
                rule.modelIds.includes(selected.id)
              )}
              canPrevious={selectedIndex > 0}
              canNext={selectedIndex >= 0 && selectedIndex < navigation.length - 1}
              onPrevious={() => {
                const previous = selectedIndex > 0 ? navigation[selectedIndex - 1] : undefined;
                if (previous) openEntry(previous);
              }}
              onNext={() => {
                const next = selectedIndex >= 0 ? navigation[selectedIndex + 1] : undefined;
                if (next) openEntry(next);
              }}
              onClose={() => setSelected(null)}
              groupContext={
                selectedGroup?.groupView
                  ? {
                      name: selectedGroup.groupView.group.name,
                      origin: (memberId) =>
                        document.querySelector(
                          `.group-members [data-model-id="${CSS.escape(memberId)}"]`
                        )
                    }
                  : undefined
              }
            />
          </Suspense>
        </DeferredChunkErrorBoundary>
      ) : null}
    </div>
  );
}
